import { useState } from "react";
import { MAD } from "../lib/api";
import { TrendingUp, AlertTriangle, ChevronDown, ChevronUp, Info } from "lucide-react";

function Cell({ label, value, accent, sub, testid }) {
  return (
    <div className="tm-card p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-[var(--tm-muted)]">{label}</p>
      <p className="font-display text-2xl mt-2 tabular" style={{ color: accent || "var(--tm-beige)" }}>{value}</p>
      {sub && <p className="text-xs text-[var(--tm-muted)] mt-1">{sub}</p>}
    </div>
  );
}

function Alerts({ a, f }) {
  const items = [];
  if (a.ventes_sans_cout) items.push({ k: "vsc", t: `${a.ventes_sans_cout} produit(s) vendu(s) sans coût (CA ${MAD(f.ventes_sans_cout_ca)}) : coût matière sous-estimé` });
  (a.produits_cout_superieur_prix || []).forEach((p) => items.push({ k: "p" + p.id, t: `Coût de « ${p.nom} » (${MAD(p.cout_revient)}) supérieur au prix de vente (${MAD(p.prix_vente)})` }));
  (a.glovo_depenses_doublon || []).forEach((x) => items.push({ k: "g" + x.expense_id, t: `Dépense « ${x.libelle} » (${MAD(x.montant)}) — relevé Glovo ${x.releve_periode} : ${x.message}` }));
  if (!items.length) return null;
  return (
    <div className="tm-card p-4 mb-3" style={{ borderColor: "#FF980066", background: "rgba(255,152,0,.06)" }} data-testid="finance-alerts">
      {items.map((x) => <p key={x.k} className="text-sm flex gap-2 py-0.5"><AlertTriangle size={14} className="mt-0.5 shrink-0" style={{ color: "#FF9800" }} />{x.t}</p>)}
    </div>
  );
}

function ChargesTable({ f }) {
  return (
    <div className="tm-card overflow-x-auto mt-3" data-testid="finance-charges-detail">
      <table className="tm-table">
        <thead><tr><th>Charge fixe</th><th className="text-right">Mensuel</th><th className="text-right">Provisionné</th><th className="text-right">Payé</th><th className="text-right">Retenu</th><th className="text-right">Reste à payer</th></tr></thead>
        <tbody>
          {f.charges_detail.map((c) => (
            <tr key={c.id}><td>{c.nom} <span className="text-[var(--tm-muted)] text-xs">{c.categorie}</span></td>
              <td className="text-right tabular">{MAD(c.mensuel)}</td><td className="text-right tabular">{MAD(c.provisionne)}</td>
              <td className="text-right tabular" style={{ color: "#4CAF50" }}>{MAD(c.paye)}</td><td className="text-right tabular">{MAD(c.retenu)}</td>
              <td className="text-right tabular" style={{ color: "#FF9800" }}>{MAD(c.restant)}</td></tr>
          ))}
        </tbody>
      </table>
      {f.salaires_detail?.length > 0 && (
        <div className="p-3 text-xs text-[var(--tm-muted)]" data-testid="finance-salaires-detail">
          Reste à verser : {f.salaires_detail.map((s) => `${s.nom} ${MAD(s.reste_a_verser)} (avances ${MAD(s.avances)})`).join(" · ")}
        </div>
      )}
    </div>
  );
}

export function FinanceBlock({ finance, title = "Pilotage financier" }) {
  const [open, setOpen] = useState(false);
  if (!finance) return null;
  const f = finance;
  const insuff = f.data_insuffisante;
  const col = (v) => (v >= 0 ? "#4CAF50" : "#F44336");
  const cm = f.cout_matiere_detail || {};
  return (
    <div data-testid="finance-block">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={18} style={{ color: "var(--tm-gold)" }} />
        <h2 className="font-display text-xl">{title}</h2>
        <span className="text-xs text-[var(--tm-muted)]">· {f.mois} · jour {f.jours_ecoules}/{f.jours_mois}</span>
      </div>
      {f.alertes && <Alerts a={f.alertes} f={f} />}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <Cell label="CA net du mois" value={MAD(f.ca_net)} sub={`CA brut ${MAD(f.ca_brut)}`} testid="fin-ca-net" />
        <Cell label="Coût matière des ventes" value={MAD(f.cout_matiere)} accent="#F44336" sub={`dont Glovo estimé ${MAD(cm.glovo_estime)} (${cm.taux_glovo_pct}% · ${cm.source_taux_glovo})`} testid="fin-cout-matiere" />
        <Cell label="Marge brute" value={MAD(f.marge_brute)} sub={f.taux_marge_pct != null ? `taux de marge brute ${f.taux_marge_pct}%` : "—"} testid="fin-marge" />
        <Cell label="Ratio achats" value={f.ratio_achats_pct != null ? `${f.ratio_achats_pct}%` : "—"} accent="#FF9800" sub={`Mat. premières + emballages ${MAD(f.achats_matieres)} ÷ CA brut`} testid="fin-ratio-achats" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <Cell label="Coûts variables (hors charges fixes)" value={MAD(f.couts_variables)} accent="#F44336" testid="fin-cv" />
        <Cell label="Charges fixes / mois" value={MAD(f.charges_fixes_mensuelles)} sub={`provisionnées ${MAD(f.charges_fixes_provisionnees)}`} testid="fin-cf-mensuelles" />
        <Cell label="Charges fixes payées" value={MAD(f.charges_fixes_payees)} accent="#4CAF50" sub={`retenues ${MAD(f.charges_fixes_retenues)}`} testid="fin-cf-payees" />
        <Cell label="Restant à payer" value={MAD(f.charges_fixes_restant)} accent="#FF9800" testid="fin-cf-restant" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Cell label="Résultat après charges fixes" value={MAD(f.resultat_apres_charges)} accent={col(f.resultat_apres_charges)} sub="CA net − coûts variables − charges retenues" testid="fin-resultat" />
        <Cell label="Résultat prévisionnel fin de mois" value={insuff ? "Données insuffisantes" : MAD(f.resultat_previsionnel)} accent={insuff ? "var(--tm-muted)" : col(f.resultat_previsionnel)} sub={`CA net projeté ${MAD(f.ca_net_projete)}`} testid="fin-previsionnel" />
        <Cell label="Seuil de rentabilité mensuel" value={f.seuil_rentabilite == null ? "Données insuffisantes" : MAD(f.seuil_rentabilite)} accent="var(--tm-gold)" sub="charges fixes ÷ taux de marge brute" testid="fin-seuil" />
        <Cell label="Pertes (indicateur)" value={MAD(f.pertes)} accent="var(--tm-muted)" sub="non déduites (déjà dans les achats)" testid="fin-pertes" />
      </div>
      <p className="text-[11px] text-[var(--tm-muted)] mt-2 flex gap-1" data-testid="fin-pertes-note"><Info size={12} className="mt-0.5 shrink-0" />{f.pertes_note}</p>
      {f.charges_detail && (
        <button className="tm-btn tm-btn-ghost mt-3" onClick={() => setOpen(!open)} data-testid="fin-detail-toggle">
          {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />} Détail des charges fixes
        </button>
      )}
      {open && <ChargesTable f={f} />}
    </div>
  );
}
